import { motion } from 'framer-motion';
import * as React from 'react';

import Toaster from '../../components/Toaster/Toaster';
import LayoutHeader from '../components/LayoutHeader/LayoutHeader';
import OverlayNavigation from '../components/OverlayNavigation/OverlayNavigation';
import postMessageToParent from '../helpers/postMessageToParent';
import usePageProps from '../hooks/Inertia/usePageProps';
import Footer from '../pages/BookingForm/components/Footer/Footer';

import LayoutContent from './components/LayoutContent/LayoutContent';
import LayoutProps from './types/LayoutProps';

import styles from './OverlayLayout.module.css';

type OverlayLayoutProps = LayoutProps & {
    onIframeOverlayClose?: () => void;
};

const OverlayLayout = ({
    children,
    inlineLoginUrl,
    inlineRequestPasswordResetUrl,
    headerButtons,
    width,
    headerOptions,
    onIframeOverlayClose,
}: OverlayLayoutProps) => {
    const { navigationUrls, flash, loggedInCustomer } = usePageProps();

    const closeIframeOverlay = () => {
        onIframeOverlayClose?.();

        postMessageToParent({
            type: 'lb-close',
        });
    };

    const filteredNavigation = navigationUrls.filter((item) => {
        if (headerOptions?.inlineLogin && item.hideWhenInlineLoginEnabled) {
            return false;
        }

        return !item.hideInOverlayMode;
    });

    return (
        <div className={styles.Root}>
            <motion.div
                className={styles.Background}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: 0.3 } }}
            />

            {/**
             * The content id is used by other components to determine the dimensions
             * of the content area. We should probably solve this in a cleaner way.
             */}
            <LayoutContent
                width={width}
                className={styles.Content}
                id="content"
            >
                <LayoutHeader
                    options={headerOptions}
                    loggedInCustomer={loggedInCustomer}
                    inlineLoginUrl={inlineLoginUrl}
                    inlineRequestPasswordResetUrl={
                        inlineRequestPasswordResetUrl
                    }
                    onClose={closeIframeOverlay}
                >
                    {headerButtons}

                    {filteredNavigation.length > 0 && (
                        <OverlayNavigation
                            navigationUrls={filteredNavigation}
                            loggedInCustomer={loggedInCustomer}
                            preventLogout={headerOptions?.preventLoginOrLogout}
                        />
                    )}
                </LayoutHeader>

                <motion.div
                    initial={{ opacity: 0, y: -50 }}
                    animate={{
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0.5, delay: 0.2 },
                    }}
                >
                    {children}
                </motion.div>

                <Footer />
            </LayoutContent>

            <Toaster>{flash.success}</Toaster>
        </div>
    );
};

export default OverlayLayout;
